import { LayoutDashboard, Shield, Database, LogOut, Bell, Settings, Users, Crown, Plus, Loader2, Sparkles, Percent } from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useIsAdmin } from '@/hooks/useAdmin';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { canUpgradeTier } from '@/lib/subscriptionTier';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePromoCopy } from '@/hooks/usePromoCopy';
import { toast } from 'sonner';

export function AppSidebar() {
  const { signOut, user } = useAuth();
  const { data: isAdmin } = useIsAdmin();
  const { data: workspaces } = useWorkspace();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const queryClient = useQueryClient();
  const promo = usePromoCopy('dashboard_sidebar');

  // State for create workspace dialog
  const [newWorkspaceDialogOpen, setNewWorkspaceDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceType, setNewWorkspaceType] = useState<'personal' | 'team'>('team');

  // Fetch user subscription
  const { data: userSubscription } = useQuery({
    queryKey: ['user-subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Create workspace mutation
  const createWorkspace = useMutation({
    mutationFn: async () => {
      if (!newWorkspaceName) throw new Error('Workspace name is required');

      const { data, error } = await supabase
        .from('workspaces')
        .insert({
          name: newWorkspaceName,
          type: newWorkspaceType,
          owner_id: user!.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Workspace created successfully');
      setNewWorkspaceDialogOpen(false);
      setNewWorkspaceName('');
      setNewWorkspaceType('team');
      // Switch to the newly created workspace
      handleWorkspaceChange(data.id);
    },
    onError: (error: any) => {
      toast.error(`Failed to create workspace: ${error.message}`);
    },
  });

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(() => {
    return localStorage.getItem('selectedWorkspaceId');
  });

  // Auto-select first workspace if none selected OR if selected workspace doesn't exist
  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      // Check if current selection is valid
      const isValidSelection = selectedWorkspaceId &&
        workspaces.some(w => w.id === selectedWorkspaceId);

      if (!isValidSelection) {
        // Either no selection or invalid selection - auto-select first workspace
        const firstWorkspaceId = workspaces[0].id;
        setSelectedWorkspaceId(firstWorkspaceId);
        localStorage.setItem('selectedWorkspaceId', firstWorkspaceId);
      }
    }
  }, [workspaces, selectedWorkspaceId]);

  const handleWorkspaceChange = (workspaceId: string) => {
    setSelectedWorkspaceId(workspaceId);
    localStorage.setItem('selectedWorkspaceId', workspaceId);
    window.location.reload();
  };

  const currentWorkspace = workspaces?.find(w => w.id === selectedWorkspaceId);

  const mainItems = [
    { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
    { title: 'Analytics Properties', url: '/data-sources', icon: Database },
    { title: 'Team Members', url: '/workspace/members', icon: Users },
  ];

  const userItems = [
    { title: 'Settings', url: '/settings', icon: Settings },
  ];

  const adminItems = isAdmin ? [
    { title: 'Admin Panel', url: '/admin', icon: Shield },
  ] : [];

  // Close sidebar on mobile when clicking a menu item
  const handleMenuClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar className="bg-background border-r border-border/60">
      <SidebarContent className="bg-background">
        {/* Logo/Brand */}
        <div className="h-[73px] px-4 sm:px-6 flex items-center border-b border-border/60">
          <NavLink to="/" className="flex items-center gap-2.5 transition-colors hover:text-primary">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white shadow-sm">
              <Shield className="h-5 w-5" />
            </div>
            <span className="font-bold text-lg">UTMGuard</span>
          </NavLink>
        </div>

        {/* Workspace Switcher */}
        <div className="p-4 pb-3 border-b border-border/60 min-h-[88px]">
          {workspaces && workspaces.length > 0 ? (
            <>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                Workspace
              </label>
              <Select value={selectedWorkspaceId || ''} onValueChange={handleWorkspaceChange}>
                <SelectTrigger className="w-full h-10 bg-muted/30 border-border/40 hover:bg-muted/50 transition-colors">
                  <SelectValue placeholder="Select workspace">
                    {currentWorkspace?.name || 'Loading...'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map((workspace: any) => (
                    <SelectItem key={workspace.id} value={workspace.id}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{workspace.name}</span>
                        <span className="text-xs text-muted-foreground capitalize">
                          {workspace.type}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  <SelectSeparator />
                  <div
                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setNewWorkspaceDialogOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2 absolute left-2" />
                    <span className="font-medium">Add New Workspace</span>
                  </div>
                </SelectContent>
              </Select>
            </>
          ) : (
            <>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                Workspace
              </label>
              <div className="w-full h-10 bg-muted/30 border border-border/40 rounded-md animate-pulse" />
            </>
          )}
        </div>

        {/* Main Navigation - Workspace Level */}
        <SidebarGroup className="px-3 py-2">
          <SidebarGroupLabel className="px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Workspace
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <NavLink
                    to={item.url}
                    end={item.url !== '/data-sources'}
                    onClick={handleMenuClick}
                  >
                    {({ isActive }) => (
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        className="relative data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-semibold data-[active=true]:before:absolute data-[active=true]:before:left-0 data-[active=true]:before:top-1 data-[active=true]:before:bottom-1 data-[active=true]:before:w-1 data-[active=true]:before:bg-primary data-[active=true]:before:rounded-r-full hover:bg-muted/50 transition-all"
                      >
                        <span className="flex items-center gap-3">
                          <item.icon className="h-4 w-4" />
                          <span className="text-sm">{item.title}</span>
                        </span>
                      </SidebarMenuButton>
                    )}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* User Navigation */}
        <SidebarGroup className="px-3 py-2">
          <SidebarGroupLabel className="px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Account
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {userItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <NavLink to={item.url} onClick={handleMenuClick}>
                    {({ isActive }) => (
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        className="relative data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-semibold data-[active=true]:before:absolute data-[active=true]:before:left-0 data-[active=true]:before:top-1 data-[active=true]:before:bottom-1 data-[active=true]:before:w-1 data-[active=true]:before:bg-primary data-[active=true]:before:rounded-r-full hover:bg-muted/50 transition-all"
                      >
                        <span className="flex items-center gap-3">
                          <item.icon className="h-4 w-4" />
                          <span className="text-sm">{item.title}</span>
                        </span>
                      </SidebarMenuButton>
                    )}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin Navigation */}
        {isAdmin && (
          <SidebarGroup className="px-3 py-2">
            <SidebarGroupLabel className="px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Admin
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-1">
                {adminItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <NavLink to={item.url} end onClick={handleMenuClick}>
                      {({ isActive }) => (
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          className="relative data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-semibold data-[active=true]:before:absolute data-[active=true]:before:left-0 data-[active=true]:before:top-1 data-[active=true]:before:bottom-1 data-[active=true]:before:w-1 data-[active=true]:before:bg-primary data-[active=true]:before:rounded-r-full hover:bg-muted/50 transition-all"
                        >
                          <span className="flex items-center gap-3">
                            <item.icon className="h-4 w-4" />
                            <span className="text-sm">{item.title}</span>
                          </span>
                        </SidebarMenuButton>
                      )}
                    </NavLink>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="border-t border-border/60 p-4 space-y-3 bg-background">
        {/* Subscription Info */}
        {userSubscription && (
          <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20 shadow-sm">
            <CardContent className="p-3.5 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10">
                    <Crown className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="text-sm font-semibold capitalize">{userSubscription.tier} Plan</span>
                </div>
                <Badge
                  variant={userSubscription.status === 'active' ? 'default' : 'secondary'}
                  className="text-xs h-5"
                >
                  {userSubscription.status}
                </Badge>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Database className="h-3 w-3" />
                <span>{userSubscription.data_sources_used} / {userSubscription.data_source_limit} Analytics Properties</span>
              </div>
              {canUpgradeTier(userSubscription.tier) && (
                <Button
                  size="sm"
                  className="w-full text-xs h-8 shadow-sm"
                  onClick={() => {
                    navigate('/settings/billing');
                    if (isMobile) {
                      setOpenMobile?.(false);
                    }
                  }}
                >
                  Upgrade Plan
                </Button>
              )}

              {promo && (
                <div className="mt-3 rounded-lg border border-primary/25 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-3 text-xs text-muted-foreground">
                  <div className="flex flex-col gap-2 flex-1">
                    <span className="inline-flex items-center gap-2 rounded-full bg-gradient-hero px-3 py-1 text-xs font-semibold text-white shadow-md">
                      <Percent className="h-3 w-3" />
                      {promo.badge || 'Offer'}
                    </span>
                    <span className="font-semibold text-foreground leading-tight text-[13px]">
                      {promo.offerLabel}{' '}
                      <span className="font-semibold text-foreground">Use code: {promo.code}</span>
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {user?.email && (
          <div className="text-xs text-muted-foreground truncate px-1">
            {user.email}
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={signOut}
          className="w-full justify-start hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <LogOut className="h-4 w-4 mr-2" />
          <span className="text-sm">Sign Out</span>
        </Button>
      </SidebarFooter>

      {/* Create Workspace Dialog */}
      <Dialog open={newWorkspaceDialogOpen} onOpenChange={setNewWorkspaceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
            <DialogDescription>
              Create a new workspace to organize your analytics properties and team members
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newWorkspaceName">Workspace Name</Label>
              <Input
                id="newWorkspaceName"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                placeholder="e.g., Marketing Team, Client Project"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newWorkspaceType">Type</Label>
              <Select
                value={newWorkspaceType}
                onValueChange={(v) => setNewWorkspaceType(v as 'personal' | 'team')}
              >
                <SelectTrigger id="newWorkspaceType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal - Just for you</SelectItem>
                  <SelectItem value="team">Team - Collaborate with others</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewWorkspaceDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => createWorkspace.mutate()}
              disabled={!newWorkspaceName || createWorkspace.isPending}
            >
              {createWorkspace.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
